import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import Checkbox from "@cloudscape-design/components/checkbox";
import Form from "@cloudscape-design/components/form";
import FormField from "@cloudscape-design/components/form-field";
import Input from "@cloudscape-design/components/input";
import Link from "@cloudscape-design/components/link";
import SpaceBetween from "@cloudscape-design/components/space-between";
import { useState } from "react";
import { api, errorMessage, writeToken } from "../../api/client";
import type { LoginResponse } from "../../api/types";
import { useValidatedForm } from "../../lib/useValidatedForm";
import { emailRules, notEmpty, type ValidationConfig } from "../../lib/validation";
import { navigate, useFollow } from "../../router/useRoute";
import { PublicShell } from "./PublicShell";
import { GoogleSignInButton } from "../../components/GoogleSignInButton";
import { readReferralCookie } from "../../lib/referral";

type Field = "email" | "password";

// Đăng nhập không kiểm tra độ mạnh mật khẩu: mật khẩu cũ có thể không đạt luật
// hiện hành, chặn ở đây sẽ khoá người dùng ra khỏi chính tài khoản của họ.
const config: ValidationConfig = {
  email: emailRules,
  password: [{ validate: notEmpty, errorText: "Mật khẩu là bắt buộc." }],
};

const FIELD_ORDER = ["email", "password"] as const satisfies readonly Field[];

export function LoginPage() {
  const follow = useFollow();
  const form = useValidatedForm<Field>(config, { email: "", password: "" }, FIELD_ORDER);
  const [remember, setRemember] = useState(false);
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setFormError("");
    if (!form.submit()) return;
    setSubmitting(true);
    try {
      const result = await api<LoginResponse>("/extension/auth/login", {
        method: "POST",
        body: {
          email: form.values.email.trim(),
          password: form.values.password,
          remember,
          installation_key: null,
          channel: "web",
        },
      });
      writeToken(result.token);
      navigate("/member");
    } catch (error) {
      // Lỗi máy chủ không gắn với field cụ thể nên hiển thị ngay trên nút gửi.
      setFormError(errorMessage(error));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <PublicShell
      title="Đăng nhập"
      description="Quản lý tài khoản FaceGo hoặc tiếp tục làm việc trên extension."
      footer={
        <Box textAlign="center">
          <SpaceBetween size="xs">
            <Link href="/forgot-password" onFollow={follow}>
              Quên mật khẩu
            </Link>
            <Box variant="span" color="text-body-secondary">
              Chưa có tài khoản?{" "}
              <Link href="/register" onFollow={follow}>
                Đăng ký FaceGo
              </Link>
            </Box>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
      <GoogleSignInButton remember={remember} referralCode={readReferralCookie()} />
      <form onSubmit={handleSubmit}>
        <Form
          errorText={formError || undefined}
          errorIconAriaLabel="Lỗi"
          actions={
            <Button variant="primary" loading={submitting} loadingText="Đang đăng nhập">
              Đăng nhập
            </Button>
          }
        >
          <SpaceBetween size="l">
            <FormField label="Email FaceGo" errorText={form.errors.email}>
              <Input
                ref={form.refFor("email")}
                type="email"
                autoComplete="email"
                placeholder="ban@example.com"
                value={form.values.email}
                onChange={({ detail }) => form.setValue("email", detail.value)}
                onBlur={() => form.handleBlur("email")}
              />
            </FormField>
            <FormField label="Mật khẩu" errorText={form.errors.password}>
              <Input
                ref={form.refFor("password")}
                type="password"
                autoComplete="current-password"
                value={form.values.password}
                onChange={({ detail }) => form.setValue("password", detail.value)}
                onBlur={() => form.handleBlur("password")}
              />
            </FormField>
            <Checkbox checked={remember} onChange={({ detail }) => setRemember(detail.checked)}>
              Duy trì đăng nhập trên thiết bị này trong 30 ngày
            </Checkbox>
          </SpaceBetween>
        </Form>
      </form>
      </SpaceBetween>
    </PublicShell>
  );
}
